import { ColumnDef } from '@tanstack/react-table';
import { getUploadWidgetIndex, NonGraniteItem } from 'api/inventory/api';
import clsx from 'clsx';
import { ServerPaginatedTable } from 'components/Table/ServerPaginatedTable';
import { usePaginatedTable } from 'hooks/usePaginatedTable';
import { CheckmarkCircle } from 'react-ionicons';
import ProgressIndicator from 'screens/InvoiceAnalyzer/ProgressIndicator';
import format from 'date-fns/format';
import { EmptyNonGraniteIndex } from 'screens/AccessExpressV3/Orders/EmptyResults';
import { useSortedTable } from 'hooks/useSortedTable';
import { UploadWidgetFilterSearchParams } from 'api/inventory/schema';
import { useCallback } from 'react';
import { InvoiceDownloadButton } from './InvoiceDownloadButton';
import ActionButtons from './ActionButtons';

const SERVICES_STALE_INTERVAL = 60_000;

export const nonGraniteColumns: ColumnDef<NonGraniteItem>[] = [
  {
    id: 'filename',
    header: 'Invoice name',
    accessorKey: 'filename',
    cell: ({ getValue, row }) => {
      if (row.original.processing_status === 'processing') {
        return <>{getValue() as string}</>;
      }
      const fileName = getValue() as string | undefined;
      const invoiceId = row.original.id;

      if (!fileName || !invoiceId) {
        return <>{fileName ?? ''}</>;
      }

      return (
        <InvoiceDownloadButton invoiceId={invoiceId} fileName={fileName} />
      );
    },
  },
  {
    id: 'filetype',
    header: 'Upload type',
    accessorKey: 'filetype',
    cell: (row) => {
      const type = row.getValue() as string;
      return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    },
  },
  {
    id: 'processing_status',
    header: 'Upload status',
    accessorKey: 'processing_status',
    cell: (row) => {
      const status = (row.getValue() as string) ?? 'Processing';

      const statusStyles: Record<string, string> = {
        Processing: 'bg-blue-400',
        'pending action': 'bg-teal-400 ',
      };

      return (
        <p className="flex items-center gap-2">
          <span
            className={clsx(
              'min-h-[10px] min-w-[10px] rounded-full',
              statusStyles[status] || 'bg-gray-200',
            )}
          />
          {status === 'pending action'
            ? 'Ready for review'
            : status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
        </p>
      );
    },
  },
  {
    id: 'progress',
    header: 'Progress',
    accessorKey: 'progress',
    cell: (row) => {
      const value = row.getValue() ?? 20;
      const status = row.row.original.processing_status ?? 'processing';
      return status === 'processing' ? (
        <ProgressIndicator
          percentage={value as number}
          barClassName={clsx('bg-blue-400')}
        />
      ) : status === 'pending action' ? (
        <div className="flex w-full items-center gap-2">
          <ProgressIndicator
            percentage={100}
            barClassName={clsx('bg-teal-400')}
          />
          <CheckmarkCircle width="20px" height="20px" color="#82F0FF" />
        </div>
      ) : null;
    },
  },
  {
    id: 'created_at',
    header: 'Date uploaded',
    accessorKey: 'created_at',
    cell: (row) => {
      const date = row.getValue() as string;
      return date ? (
        <span className="text-base font-medium">
          {format(new Date(date), 'MM/dd/yyyy')}
        </span>
      ) : (
        '-- -- --'
      );
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    accessorKey: 'actions',
    cell: ({ row }) => (
      <ActionButtons
        status={row.original.processing_status}
        id={row.original.id}
      />
    ),
  },
];

const UploadWidgetTable = () => {
  const { sortingQueryParams, sortingState, onSortingChange } = useSortedTable({
    initialSorting: [{ id: 'created_at', desc: true }],
  });

  const buildUploadWidgetQueryParams = useCallback(
    (args?: UploadWidgetFilterSearchParams) => ({
      ...sortingQueryParams,

      ...args,
    }),
    [sortingQueryParams],
  );

  const getUploadWidgetIndexFn = useCallback(
    (args?: UploadWidgetFilterSearchParams) => {
      return getUploadWidgetIndex(buildUploadWidgetQueryParams(args));
    },
    [buildUploadWidgetQueryParams],
  );

  const { data: nonGranitePageData, ...nonGranitePaginatedProps } =
    usePaginatedTable(
      getUploadWidgetIndexFn,
      {
        sortingQueryParams,
      },
      ['upload-widget-table', sortingQueryParams],
      {
        refetchOnMount: true,
        staleTime: SERVICES_STALE_INTERVAL,
      },
      { pageSize: 5, pageIndex: 0 },
    );

  return (
    <div className="bg-non-granite-table w-full rounded border p-6">
      <ServerPaginatedTable
        data={nonGranitePageData ?? []}
        columns={nonGraniteColumns}
        title="Non granite services"
        sortingState={sortingState}
        onSortingChange={onSortingChange}
        emptyDataElement={<EmptyNonGraniteIndex />}
        hidePageSizeSelection={true}
        showPagination={nonGranitePaginatedProps.itemCount > 5}
        showRowCount={false}
        {...nonGranitePaginatedProps}
        isFetchingData={nonGranitePaginatedProps.isFetchingData}
        handleRowClick={() => {
          // TODO: open modal with details
          // console.log('row', row);
          // open();
        }}
        columnGap="48px"
      />
    </div>
  );
};
export default UploadWidgetTable;
